import React, { memo } from 'react';
import { XCircle, AlertCircle, Target, Clock, GraduationCap, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function ProblemAudience() {
  const painPoints = [{
    title: 'Llegás al parcial sin saber qué entra',
    desc: 'Sin saber qué temas priorizar ni cuáles artículos el profe siempre pregunta.'
  }, {
    title: 'Estudiaste horas y te fue igual',
    desc: 'Sin método claro, las horas de estudio no se traducen en nota.'
  }, {
    title: 'El oral te paraliza',
    desc: 'No sabés cómo estructurar la respuesta ni qué hacer cuando te quedás en blanco.'
  }, {
    title: 'Siempre los mismos apuntes básicos',
    desc: 'Sin entender la conexión entre los temas, cada materia parece un tema suelto.'
  }, {
    title: 'Los artículos del CCCN no te quedan',
    desc: 'Te sabés la teoría pero no podés citar el artículo que la respalda.'
  }, {
    title: 'Tips sueltos de compañeros',
    desc: 'Sin un método claro que te dé seguridad sobre qué estudiar y qué no.'
  }, {
    title: 'No sabés resolver casos prácticos',
    desc: 'Cuando el profe da un caso, no sabés por dónde empezar el análisis.'
  }, {
    title: 'Fin de cuatrimestre sin aprobar',
    desc: 'Sin sistema, terminás el cuatrimestre con las mismas materias pendientes.'
  }];
  return <section className="py-12 md:py-20 bg-white" data-id="element-270">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-271">
        {/* Problem Section */}
        <div className="mb-16 md:mb-24" data-id="element-272">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8 md:mb-12" data-id="element-273">
            <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-2 md:mb-4" data-id="element-274">
              ¿TE PASA ESTO?
            </h2>
            <p className="text-base md:text-xl text-slate-600" data-id="element-275">
              Si estudiás Abogacía en Argentina, esto te suena familiar:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6" data-id="element-276">
            {painPoints.map((point, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} whileHover={{
            scale: 1.02
          }} className="flex gap-3 md:gap-4 p-4 md:p-5 rounded-xl bg-red-50/50 border border-red-100 hover:bg-red-50 transition-colors cursor-default" data-id="element-277">
                <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500 flex-shrink-0 mt-0.5 md:mt-1" data-id="element-278" />
                <div data-id="element-279">
                  <h3 className="font-bold text-sm md:text-base text-slate-900 mb-0.5 md:mb-1" data-id="element-280">
                    {point.title}
                  </h3>
                  <p className="text-slate-600 text-xs md:text-sm" data-id="element-281">
                    {point.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mt-8 md:mt-12 text-center" data-id="element-282">
            <p className="text-lg md:text-2xl font-bold text-slate-900" data-id="element-283">
              ¿Cuántos cuatrimestres más vas a estudiar sin método?
            </p>
          </motion.div>
        </div>

        {/* Audience Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-slate-900 rounded-3xl p-6 md:p-12 text-white shadow-xl" data-id="element-284">
          <div className="text-center mb-8 md:mb-10" data-id="element-285">
            <h2 className="text-2xl md:text-4xl font-extrabold mb-2 md:mb-4" data-id="element-286">
              ¿PARA QUIÉN ES?
            </h2>
            <p className="text-base md:text-xl text-slate-300" data-id="element-287">
              Diseñado para vos si querés aprobar Contratos y Obligaciones de
              una vez
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8" data-id="element-288">
            <motion.div whileHover={{
            x: 5
          }} className="flex gap-3 md:gap-4" data-id="element-289">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-500/20 flex items-center justify-center flex-shrink-0" data-id="element-290">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-brand-400" data-id="element-291" />
              </div>
              <div data-id="element-292">
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2" data-id="element-293">
                  Estudiantes estancados
                </h3>
                <p className="text-sm md:text-base text-slate-300" data-id="element-294">
                  Cursaste Contratos una o dos veces y seguís sin aprobar.
                  Necesitás un método, no más horas de estudio.
                </p>
              </div>
            </motion.div>

            <motion.div whileHover={{
            x: 5
          }} className="flex gap-3 md:gap-4" data-id="element-295">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-500/20 flex items-center justify-center flex-shrink-0" data-id="element-296">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-brand-400" data-id="element-297" />
              </div>
              <div data-id="element-298">
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2" data-id="element-299">
                  Estudiás y trabajás
                </h3>
                <p className="text-sm md:text-base text-slate-300" data-id="element-300">
                  Con poco tiempo disponible, necesitás saber exactamente qué
                  estudiar para no perder ni un minuto.
                </p>
              </div>
            </motion.div>

            <motion.div whileHover={{
            x: 5
          }} className="flex gap-3 md:gap-4" data-id="element-301">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-500/20 flex items-center justify-center flex-shrink-0" data-id="element-302">
                <GraduationCap className="w-5 h-5 md:w-6 md:h-6 text-brand-400" data-id="element-303" />
              </div>
              <div data-id="element-304">
                <div className="flex items-center gap-2 mb-1 md:mb-2" data-id="element-305">
                  <h3 className="font-bold text-base md:text-lg" data-id="element-306">
                    Estudiantes que van al oral
                  </h3>
                  <span className="px-2 py-0.5 text-[10px] md:text-xs font-bold bg-accent-500 text-white rounded-full" data-id="element-307">
                    NUEVO
                  </span>
                </div>
                <p className="text-sm md:text-base text-slate-300" data-id="element-308">
                  Necesitás estructura para responder con seguridad frente al
                  profesor sin bloquearte.
                </p>
              </div>
            </motion.div>

            <motion.div whileHover={{
            x: 5
          }} className="flex gap-3 md:gap-4" data-id="element-309">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-500/20 flex items-center justify-center flex-shrink-0" data-id="element-310">
                <Target className="w-5 h-5 md:w-6 md:h-6 text-brand-400" data-id="element-311" />
              </div>
              <div data-id="element-312">
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2" data-id="element-313">
                  Buscás aprobar con buena nota
                </h3>
                <p className="text-sm md:text-base text-slate-300" data-id="element-314">
                  Vas por el 7 o el 8, no solo por el 4. Querés entender la
                  materia, no memorizarla.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-slate-800 text-center" data-id="element-315">
            <a href="#pricing" className="inline-flex items-center gap-2 text-sm md:text-base text-brand-400 hover:text-brand-300 font-medium transition-colors group" data-id="element-316">
              ¿No estás seguro si es para vos? Tenés 7 días de garantía{' '}
              <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-317" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}